import React, { useMemo, useCallback, useState, useContext, memo, useEffect, useRef, useImperativeHandle, forwardRef } from "react"
import Box from "@material-ui/core/Box"
import Typography from "@material-ui/core/Typography"
import { makeStyles } from "@material-ui/core/styles"
import FormControl from "@material-ui/core/FormControl"
import RadioGroup from "@material-ui/core/RadioGroup"
import Radio from "@material-ui/core/Radio"
import FormControlLabel from "@material-ui/core/FormControlLabel"
import clsx from "clsx"
import { sortObjArray, groupArray } from "@/utils"
import AssessmentContext from "@/components/assessment/AssessmentContext"
import Button from "@material-ui/core/Button"
import useFindNextQuestionIndex from "@/hooks/useFindNextQuestionIndex"
import useFinalResponseAssessmentContent from "@/hooks/useFinalResponseAssessmentContent"
import { bulkSaveResponseCloseWindow } from "@/helpers/saveResponse"
import globalUseStyles from "@/globalUseStyles"
import { updateParticipantByUUID, bulkCreateResponse } from "@/apisParticipantPublic"
import RequestSnackbar from "@/components/common/RequestSnackbar"
import useHandleRequestSnackbar from "@/hooks/useHandleRequestSnackbar"
import useHandleModalShow from "@/hooks/useHandleModalShow"
import ModalNAChoice from "@/components/assessment/ModalNAChoice"

const useStyles = makeStyles((theme) => ({
    assessmentContentWrapper: {
        maxWidth: "1200px",
        margin: "100px auto",
        padding: theme.spacing(2),
    },
    questionBlock: {
        marginBottom: theme.spacing(10),
        "&:last-child": {
            marginBottom: 0,
        },
    },
    questionWrapper: {
        marginBottom: theme.spacing(4),
    },
    choiceBlockWrapper: {
        display: "flex",
        flexWrap: "wrap",
    },
    questionTitle: {
        fontSize: "1.4rem",
    },
    choiceOuterWrapper: {
        padding: theme.spacing(2),
    },
    radioGroup: {
        display: "flex",
        flexWrap: "wrap",
        flexDirection: "row",
        justifyContent: "center",
    },
    radioLabelWrapper: {
        padding: theme.spacing(0, 2),
        "&>label>span": {
            height: "100%",
            display: "flex",
            alignItems: "center",
            border: "1px solid",
            borderRadius: "5px",
            borderColor: theme.palette.primary.main,
            padding: theme.spacing(2),
            justifyContent: "center",
            cursor: "pointer",
            width: "100%",
        },
    },
    radioLabelNormalWrapper: {
        [theme.breakpoints.up("sm")]: {
            flexBasis: "calc(100% / 3)",
            margin: 0,
        },
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        minHeight: "58px",
    },
    radioLabelNAWrapper: {
        flexBasis: "200px",
        margin: theme.spacing(3, 0, 0),
        justifyContent: "center",
        "& $choiceNAWrapper": {
            width: "200px",
        },
    },
    radioLabel: {
        textAlign: "center",
        height: "100%",
        width: "100%",
        "&>.Mui-checked~.MuiFormControlLabel-label": {
            backgroundColor: theme.palette.primary.main,
            color: "white",
        },
        "& .MuiRadio-root": {
            display: "none",
        },
    },
    manualSaveButton: {
        position: "fixed",
        right: "10px",
        bottom: "25%",
        zIndex: "2",
        [theme.breakpoints.up("lg")]: {
            bottom: "15%",
        },
    },
    autoSaveText: {
        position: "fixed",
        right: "10px",
        top: "30%",
        backgroundColor: "white",
        border: "1px solid black",
    },
    finishContent: {
        border: "1px solid #eeeeef",
        background: "#fff",
        boxShadow: "0 15px 40px -15px rgb(0 0 0 / 40%)",
        padding: theme.spacing(5, 3),
        "& p": {
            fontSize: "110%",
        },
    },
}))

/**
 * should use a bulk update/create rather than update/create response one by one,
 * it's saving the api call with a bulk approach
 */

const createResponseList = (responseList) => {
    // only return a result instead of doing an api call
    const categoryResponseList = groupArray(responseList, "status", {})
    const responseListCreate = categoryResponseList?.create
    const responseListUpdate = categoryResponseList?.update
    return { responseListCreate, responseListUpdate }
}

const AssessmentContent = memo(
    forwardRef((props, ref) => {
        const { questionsDetail, responses, getFinalResponse } = props
        const globalClasses = globalUseStyles()
        const classes = useStyles()

        const { participantDetail, updateParticipantDetail } = useContext(AssessmentContext)

        const { finalResponseAssessmentContent, setFinalResponseAssessmentContent } = useFinalResponseAssessmentContent(
            sortObjArray(responses, "questionId")
        )

        const [bulkListResult, setBulkListResult] = useState({
            resultCreate: [],
            resultUpdate: [],
        })

        const [saveError, SetSaveError] = useState({
            error: null,
            errorMsg: "",
        })

        const [updateResponseBegin, setUpdateResponseBegin] = useState(false)

        const saveErrorSnackbarValue = useHandleRequestSnackbar(saveError)

        // get the index of next question
        useFindNextQuestionIndex(questionsDetail, finalResponseAssessmentContent)

        useEffect(() => {
            getFinalResponse(finalResponseAssessmentContent)
        }, [responses, finalResponseAssessmentContent])

        const assessmentContentWrapperRef = useRef()
        useImperativeHandle(ref, () => ({
            getAssessmentWrapperElement: () => {
                return assessmentContentWrapperRef.current
            },
        }))

        // handle choose choice
        const { modalItemShow: modalShow, handleClose, handleModalItemShowChange: handleModalShowChange } = useHandleModalShow()

        const handleChoiceChange = useCallback((e, responseContent, questionId) => {
            const newChoose = parseInt(e.target.value, 10)
            const newFinalResponse = JSON.parse(JSON.stringify(responseContent))
            const updatedIndex = newFinalResponse.findIndex((response) => response.questionId === questionId)
            if (updatedIndex !== -1) {
                const updateItem = newFinalResponse[updatedIndex]
                updateItem.choiceId = newChoose
                if (updateItem.status !== "create") {
                    updateItem.status = "update"
                }
                setFinalResponseAssessmentContent(newFinalResponse)
            } else {
                // add new
                const newItem = {
                    choiceId: newChoose,
                    questionId,
                    status: "create",
                }
                setFinalResponseAssessmentContent(sortObjArray(newFinalResponse.concat(newItem), "questionId"))
            }
            // trigger auto save when actions on choice change
            setUpdateResponseBegin(true)
        }, [])

        const [modalQuestionDetail, setModalQuestionDetail] = useState({
            event: null,
            questionId: null,
        })
        const handleChoose = useCallback(
            (e, questionId) => {
                if (e.target.name === "radioNA") {
                    setModalQuestionDetail({
                        event: e,
                        questionId,
                    })
                    handleModalShowChange(true)
                } else {
                    handleChoiceChange(e, finalResponseAssessmentContent, questionId)
                }
            },
            [finalResponseAssessmentContent, handleChoiceChange]
        )

        useEffect(() => {
            const { resultCreate, resultUpdate } = bulkListResult
            // update finalResponseAssessmentContent
            const result = [
                ...resultCreate.map((createItem) => ({
                    choiceId: createItem.choiceId,
                    questionId: createItem.questionId,
                    responseId: createItem.responseId,
                    status: "responded",
                })),
                ...resultUpdate.map((updatedItem) => ({
                    choiceId: updatedItem.choiceId,
                    questionId: updatedItem.questionId,
                    responseId: updatedItem.responseId,
                    status: "responded",
                })),
            ]
            if (result.length) {
                const resultSort = result.sort((a, b) => a.questionId - b.questionId)
                // compare and update finalResponseAssessmentContent
                setFinalResponseAssessmentContent((prev) => {
                    let index = 0
                    const finalResponse = [...prev]
                    const finalResult = finalResponse.map((finalResponseSingle) => {
                        if (index < result.length && finalResponseSingle.questionId === resultSort[index].questionId) {
                            return resultSort[index++]
                        }
                        return finalResponseSingle
                    })
                    return finalResult
                })
                // reset
                setUpdateResponseBegin(false)
                setBulkListResult({
                    resultCreate: [],
                    resultUpdate: [],
                })
            }
        }, [bulkListResult])

        const assessmentDetail = useMemo(() => {
            return questionsDetail.map((questionDetailItem) => {
                const { questionId } = questionDetailItem.question
                const { choices } = questionDetailItem
                const responseFiltered = finalResponseAssessmentContent.find((responseItem) => {
                    return responseItem.questionId === questionId
                })
                return {
                    question: questionDetailItem.question,
                    choices,
                    response: responseFiltered,
                }
            })
        }, [questionsDetail, finalResponseAssessmentContent])

        const handleManualSave = useCallback(async () => {
            const {
                participantId,
                projects: { companyId, projectId },
            } = participantDetail
            const { responseListCreate = [], responseListUpdate = [] } = createResponseList(finalResponseAssessmentContent)
            try {
                const { data } = await bulkCreateResponse({
                    participantId,
                    projectId,
                    companyId,
                    newResponseList: responseListCreate,
                    updateList: responseListUpdate,
                })
                if (data.bulkActionError) {
                    SetSaveError({
                        error: true,
                        errorMsg: "Save has error",
                    })
                    return
                }
                SetSaveError({
                    error: false,
                    errorMsg: "",
                })
                // update finalResponseAssessmentContent
                setBulkListResult({
                    resultCreate: data.resultCreate,
                    resultUpdate: data.resultUpdate,
                })
            } catch (e) {
                SetSaveError({
                    error: true,
                    errorMsg: "Save has error",
                })
            }
        }, [finalResponseAssessmentContent, participantDetail])

        // auto save
        const [autoSaveTrigger, setAutoSaveTrigger] = useState(false)
        const finalResponseAssessmentContentRef = useRef(finalResponseAssessmentContent)
        const participantDetailRef = useRef(participantDetail)

        useEffect(() => {
            finalResponseAssessmentContentRef.current = finalResponseAssessmentContent
        }, [finalResponseAssessmentContent])

        useEffect(() => {
            participantDetailRef.current = participantDetail
        }, [participantDetail])

        useEffect(() => {
            const handleIntervalSave = async () => {
                const {
                    participantId,
                    projects: { companyId, projectId },
                } = participantDetailRef.current
                const { responseListCreate = [], responseListUpdate = [] } = createResponseList(finalResponseAssessmentContentRef.current)
                try {
                    const { data } = await bulkCreateResponse({
                        participantId,
                        projectId,
                        companyId,
                        newResponseList: responseListCreate,
                        updateList: responseListUpdate,
                    })
                    if (!data.bulkActionError) {
                        // update finalResponseAssessmentContent
                        setBulkListResult({
                            resultCreate: data.resultCreate,
                            resultUpdate: data.resultUpdate,
                        })
                    }
                } catch {
                    // catch error
                }
            }
            let timer
            if (updateResponseBegin) {
                timer = setInterval(() => {
                    setAutoSaveTrigger(true)
                    handleIntervalSave()
                    setTimeout(() => {
                        setAutoSaveTrigger(false)
                    }, 2000)
                }, 1000 * 60 * 2)
                // 1000 = 1s, auto save every 2 mins
            }
            return () => {
                if (timer) clearInterval(timer)
            }
        }, [updateResponseBegin])

        // on close window
        const onUnload = () => {
            bulkSaveResponseCloseWindow(finalResponseAssessmentContentRef.current, participantDetailRef.current)
        }
        useEffect(() => {
            window.addEventListener("beforeunload", onUnload)
            return () => {
                window.removeEventListener("beforeunload", onUnload)
            }
        }, [])

        const [confirmError, setConfirmError] = useState({
            error: false,
            errorMsg: "",
        })
        const confirmSnackbarValue = useHandleRequestSnackbar(confirmError)

        const handleConfirmFinish = useCallback(async () => {
            const { participantUUID } = participantDetail
            // save answer
            await handleManualSave()
            // update complete status
            try {
                if (questionsDetail.length === finalResponseAssessmentContent.length) {
                    await updateParticipantByUUID(participantUUID, { complete: 1 })
                    if (updateParticipantDetail) updateParticipantDetail({ complete: 1 })
                    setConfirmError({
                        error: false,
                        errorMsg: "",
                    })
                    window.location.href = process.env.CC_MAIN_SITE
                }
            } catch (error) {
                setConfirmError({
                    error: true,
                    errorMsg: error?.data?.message || error.data,
                })
            }
        }, [participantDetail, questionsDetail, finalResponseAssessmentContent])

        const finishContent = useMemo(() => {
            return questionsDetail.length === finalResponseAssessmentContent.length ? (
                <Box align="center" className={classes.finishContent}>
                    <Box>
                        <Typography>
                            Congratulations on completing your part in the SPARCA ENTERPRISE assessment. Your answers will be combined with
                            other responses which will help us develop a group consensus of the health of your Customer Information
                            Ecosystem.
                        </Typography>
                        <Typography className={globalClasses.paragraph}>
                            If you have an interest in the results, please contact the project sponsor.
                        </Typography>
                        <Typography className={globalClasses.paragraph}>Thank you,</Typography>
                        <Typography className={globalClasses.paragraph}>Customer Crunch Team</Typography>
                    </Box>
                    <Box mt={4}>
                        <Button onClick={handleConfirmFinish} variant="contained" color="primary">
                            Confirm Finish
                        </Button>
                    </Box>
                </Box>
            ) : null
        }, [questionsDetail, finalResponseAssessmentContent])

        return (
            <Box className={classes.assessmentContentWrapper} ref={assessmentContentWrapperRef}>
                <Box mb={10}>{finishContent}</Box>
                <Box>
                    {assessmentDetail.map((assessmentDetailItem, index) => {
                        const { questionId } = assessmentDetailItem.question
                        return (
                            <Box className={classes.questionBlock} key={questionId}>
                                <Box className={classes.questionWrapper}>
                                    <Typography className={classes.questionTitle}>
                                        {(index + 1).toLocaleString("en-US", { minimumIntegerDigits: 2, useGrouping: false })}.&nbsp;&nbsp;
                                        {assessmentDetailItem.question.questionText}
                                    </Typography>
                                </Box>
                                <Box className={classes.choiceBlockWrapper}>
                                    <FormControl component="fieldset">
                                        <RadioGroup
                                            className={classes.radioGroup}
                                            aria-label="choice"
                                            name="choice"
                                            value={assessmentDetailItem.response?.choiceId || 0}
                                            onChange={(e) => handleChoose(e, questionId)}
                                        >
                                            {assessmentDetailItem.choices.map((choice) => {
                                                if (!choice.scoreType)
                                                    return (
                                                        <Box
                                                            className={clsx(classes.radioLabelWrapper, classes.radioLabelNormalWrapper)}
                                                            key={choice.choiceId}
                                                        >
                                                            <FormControlLabel
                                                                className={clsx(classes.radioLabel, classes.radioLabelNormal)}
                                                                value={choice.choiceId}
                                                                control={<Radio name="radioNormal" />}
                                                                // eslint-disable-next-line react/jsx-no-duplicate-props
                                                                label={
                                                                    <Box
                                                                        className={clsx(classes.choiceNormalWrapper, classes.choiceWrapper)}
                                                                    >
                                                                        <Box className={classes.choiceInnerWrapper}>
                                                                            {choice.choiceText}
                                                                        </Box>
                                                                    </Box>
                                                                }
                                                            />
                                                        </Box>
                                                    )
                                                return (
                                                    <Box
                                                        className={clsx(classes.radioLabelWrapper, classes.radioLabelNAWrapper)}
                                                        key={choice.choiceId}
                                                    >
                                                        <FormControlLabel
                                                            className={clsx(classes.radioLabel, classes.radioLabelNA)}
                                                            value={choice.choiceId}
                                                            control={<Radio name="radioNA" />}
                                                            // eslint-disable-next-line react/jsx-no-duplicate-props
                                                            label={
                                                                <Box className={clsx(classes.choiceNAWrapper, classes.choiceWrapper)}>
                                                                    <Box className={classes.choiceInnerWrapper}>{choice.choiceText}</Box>
                                                                </Box>
                                                            }
                                                        />
                                                    </Box>
                                                )
                                            })}
                                        </RadioGroup>
                                    </FormControl>
                                </Box>
                            </Box>
                        )
                    })}
                </Box>
                <Box className={classes.manualSaveButton}>
                    <Button onClick={handleManualSave} variant="contained" color="primary" style={{ maxWidth: "60px", minWidth: "60px" }}>
                        Save
                    </Button>
                </Box>
                <Box mt={10}>{finishContent}</Box>
                {autoSaveTrigger ? <Box className={classes.autoSaveText}>Auto Save answer...</Box> : null}
                {confirmError.errorMsg && (
                    <RequestSnackbar
                        status={false}
                        message={confirmError.errorMsg}
                        snackbarOpen={confirmSnackbarValue.snackbarOpen}
                        handleSnackbarOpenStatus={confirmSnackbarValue.handleSnackbarOpenStatus}
                    />
                )}
                {saveError.error !== null ? (
                    <>
                        {saveError.error ? (
                            <RequestSnackbar
                                status={false}
                                message={saveError.errorMsg}
                                snackbarOpen={saveErrorSnackbarValue.snackbarOpen}
                                handleSnackbarOpenStatus={saveErrorSnackbarValue.handleSnackbarOpenStatus}
                            />
                        ) : (
                            <RequestSnackbar
                                status
                                message="Success"
                                snackbarOpen={saveErrorSnackbarValue.snackbarOpen}
                                handleSnackbarOpenStatus={saveErrorSnackbarValue.handleSnackbarOpenStatus}
                            />
                        )}
                    </>
                ) : null}
                <ModalNAChoice
                    modalShow={modalShow}
                    handleClose={handleClose}
                    handleChoiceChange={handleChoiceChange}
                    finalResponseAssessmentContent={finalResponseAssessmentContent}
                    modalQuestionDetail={modalQuestionDetail}
                />
            </Box>
        )
    })
)

export default AssessmentContent
